import React from "react";

const painPoints = [
    {
        icon: "🔑",
        title: "Resetting passwords",
        desc: "The same request, ten times a day, every single day.",
    },
    {
        icon: "🖨️",
        title: "Fixing printers",
        desc: "Paper jams and driver conflicts that no one else wants to touch.",
    },
    {
        icon: "✉️",
        title: "Outlook issues",
        desc: "Closing the same email tickets over and over with zero growth.",
    },
    {
        icon: "💻",
        title: "Laptop formatting",
        desc: "Burning OS images and reinstalling software all day long.",
    },
];

const stages = [
    {
        tag: "Today",
        title: "Desktop Support Engineer",
        salary: "₹2.5L – ₹5L",
        line: "Stuck doing repetitive tickets, no real growth, watching the network team solve the interesting problems.",
        skills: ["Password Resets", "Printer Issues", "Outlook Support", "Laptop Formatting"],
        tone: "gray",
    },
    {
        tag: "After 7 Days",
        title: "Enterprise Networking Confidence",
        salary: "Your Turning Point",
        line: "You finally understand how enterprise networks actually work — and you can talk about it like an engineer.",
        skills: ["Think Like a Network Engineer", "Hands-On Practical Skills", "Interview Confidence", "Real Troubleshooting"],
        tone: "amber",
    },
    {
        tag: "Career Accelerator",
        title: "Job-Ready Network Engineer",
        salary: "₹6L – ₹10L",
        line: "Confidently walking into interviews and explaining the exact networks you built with your own hands.",
        skills: ["Enterprise Topologies", "Routing & Switching", "VLANs & Subnetting", "Interview-Ready Projects"],
        tone: "brand",
    },
    {
        tag: "The Next Levels",
        title: "Firewall Engineer → Cloud Engineer",
        salary: "₹12L – ₹20L+",
        line: "A specialist companies fight to hire — leading the infrastructure others depend on.",
        skills: ["Next-Gen Firewalls", "Cloud Networking (AWS/Azure)", "Network Security", "High-Demand Specialist Roles"],
        tone: "brand-strong",
    },
];

const toneChip = {
    gray: "text-dim border-[var(--border)] bg-slate-50",
    amber: "text-[#ffb020] border-[rgba(255,176,32,0.35)] bg-[rgba(255,176,32,0.05)]",
    brand: "text-brand border-[rgba(124,231,59,0.35)] bg-[rgba(124,231,59,0.06)]",
    "brand-strong": "text-brand border-[rgba(124,231,59,0.55)] bg-[rgba(124,231,59,0.10)]",
};

const Transformation = () => {
    return (
        <>
            {/* Pain agitator */}
            <section className="section" id="pain" data-testid="pain-section">
                <div className="container-p">
                    <div className="max-w-3xl">
                        <span className="eyebrow gray">Sound Familiar?</span>
                        <h2 className="mt-4 font-display text-3xl md:text-5xl font-extrabold leading-tight text-slate-900">
                            Does This Sound Like Your{" "}
                            <span className="text-brand">Daily Job?</span>
                        </h2>
                        <p className="mt-4 text-dim text-base md:text-lg">
                            You didn't get into IT to reset passwords forever. Yet every day you watch the
                            network team solve the real problems — and grow.
                        </p>
                    </div>

                    <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                        {painPoints.map((p) => (
                            <div key={p.title} className="panel panel-hover p-4" data-testid={`pain-${p.title.toLowerCase().replace(/\s+/g,"-")}`}>
                                <div className="text-3xl">{p.icon}</div>
                                <h3 className="mt-3 font-display font-bold text-slate-900 text-lg">
                                    {p.title}
                                </h3>
                                <p className="mt-2 text-sm text-dim leading-relaxed">{p.desc}</p>
                            </div>
                        ))}
                    </div>

                    <div className="mt-4 font-mono text-xs md:text-sm text-mute text-center md:text-left">
                        Same salary · Same tickets · Same role, year after year.
                    </div>

                    {/* But every day panel */}
                    <div className="mt-8 panel p-5 md:p-8 relative overflow-hidden">
                        <div
                            className="absolute -top-14 -right-14 w-64 h-64 rounded-full pointer-events-none"
                            style={{
                                background: "radial-gradient(circle, rgba(124,231,59,0.18), transparent 65%)",
                            }}
                            aria-hidden
                        />
                        <div className="grid md:grid-cols-[1fr_auto] items-center gap-6">
                            <div>
                                <div className="font-mono text-xs uppercase tracking-widest text-brand">
                                    But Every Day...
                                </div>
                                <h3 className="mt-2 font-display text-2xl md:text-3xl font-bold text-slate-900 leading-tight">
                                    You watch the Network Team solve the real problems.
                                </h3>
                                <p className="mt-3 text-dim">
                                    They build. They troubleshoot. They grow. They earn more.{" "}
                                    <span className="text-slate-900">You deserve that opportunity too.</span>
                                </p>
                            </div>
                            <a
                                href="#pricing"
                                className="btn-primary"
                                data-testid="pain-cta"
                            >
                                Show me the way →
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* Career blueprint */}
            <section className="section" id="blueprint" data-testid="blueprint-section">
                <div className="container-p">
                    <div className="max-w-3xl">
                        <span className="eyebrow">The Career Blueprint</span>
                        <h2 className="mt-4 font-display text-3xl md:text-5xl font-extrabold leading-tight text-slate-900">
                            This Is Where Your Career{" "}
                            <span className="text-brand">Can Go.</span>
                        </h2>
                        <p className="mt-4 text-dim text-base md:text-lg">
                            You are closer to a networking career than you think. Here is the exact path
                            most of our students follow.
                        </p>
                    </div>

                    <ol className="mt-10 relative border-l border-dashed border-[var(--border)] pl-6 md:pl-10 space-y-6">
                        {stages.map((s, i) => (
                            <li key={i} className="relative" data-testid={`stage-${i + 1}`}>
                                <div
                                    className="absolute -left-[34px] md:-left-[46px] top-3 w-8 h-8 rounded-full border border-[var(--border)] bg-[var(--panel)] font-mono text-xs flex items-center justify-center text-brand"
                                    aria-hidden
                                >
                                    0{i + 1}
                                </div>
                                <div className="panel panel-hover p-5">
                                    <div className="flex flex-wrap items-center gap-3">
                                        <span className={`font-mono text-[11px] uppercase tracking-widest px-2 py-1 rounded border ${toneChip[s.tone]}`}>
                                            {s.tag}
                                        </span>
                                        <span className="font-mono text-xs text-mute">{s.salary}</span>
                                    </div>
                                    <h3 className="mt-3 font-display text-xl md:text-2xl font-bold text-slate-900 leading-tight">
                                        {s.title}
                                    </h3>
                                    <p className="mt-2 text-dim italic">"{s.line}"</p>
                                    <div className="mt-4">
                                        <div className="font-mono text-[11px] uppercase tracking-widest text-mute mb-2">
                                            Core Focus / Skills
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            {s.skills.map((k) => (
                                                <span
                                                    key={k}
                                                    className={`text-xs md:text-sm px-2.5 py-1 rounded-md border ${toneChip[s.tone]}`}
                                                >
                                                    {k}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ol>

                    <p className="mt-6 max-w-3xl text-sm text-mute leading-relaxed">
                        We'll never promise you a Network Engineer job in 7 days. What we promise is real:{" "}
                        <span className="text-slate-900">enterprise networking confidence</span>, the ability
                        to <span className="text-slate-900">think like a network engineer</span>, hands-on
                        experience, and the interview confidence that actually gets you hired.
                    </p>
                </div>
            </section>
        </>
    );
};

export default Transformation;
